import { useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { fetchTreeSpecies } from '../../../api/settings';
import {
  fetchWorkshopType,
  WORKSHOP_TYPES_QUERY_KEY,
  type WorkshopType,
} from '../../../api/workshop-types';

import WorkshopTypeEditView from './components/WorkshopTypeEditView';
import {
  persistWorkshopType,
  useWorkshopTypeForm,
  workshopTypeErrorMessage,
} from './WorkshopTypeFormFields';

/**
 * WorkshopTypeEditPage — settings detail/create page for one workshop type,
 * reconciled to the honest ops DTO + ops CRUD (BON-117). Region-price overrides
 * are GONE (the warehouse-only concept is dropped); price is integer cents,
 * species is the ops `treeSpecies` relation. Create and update go through the
 * ops `/workshop-types` endpoints (PATCH on update).
 *
 * A successful save invalidates the shared catalogue query key (BON-391) so the
 * list behind this page and the dashboards' type pickers reflect the edit
 * immediately.
 */
export default function WorkshopTypeEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const { form, fields, seed, setTreeSpeciesList } = useWorkshopTypeForm();

  useEffect(() => {
    if (isNew) {
      fetchTreeSpecies()
        .then(setTreeSpeciesList)
        .catch(() => {});
      return;
    }
    Promise.all([fetchWorkshopType(id!), fetchTreeSpecies()])
      .then(([type, species]) => {
        setTreeSpeciesList(species);
        seed(type);
      })
      .catch(() => setLoadError('Failed to load workshop type'))
      .finally(() => setLoading(false));
  }, [id, isNew, seed, setTreeSpeciesList]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    setSaveError('');
    try {
      const saved = await persistWorkshopType(isNew ? null : ({ id: id! } as WorkshopType), form);
      await queryClient.invalidateQueries({ queryKey: WORKSHOP_TYPES_QUERY_KEY });
      if (isNew) navigate(`/admin/settings/workshop-types/${saved.id}`, { replace: true });
    } catch (err) {
      setSaveError(workshopTypeErrorMessage(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-stone-500 dark:text-stone-400 text-sm">Loading...</div>
    );
  }
  if (loadError) {
    return <div className="py-12 text-center text-red-500 text-sm">{loadError}</div>;
  }

  return (
    <WorkshopTypeEditView
      isNew={isNew}
      name={form.name}
      saving={saving}
      saveError={saveError}
      onBack={() => navigate('/admin/settings/workshop-types')}
      onSubmit={handleSave}
      fields={fields}
    />
  );
}
